import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, FileText, ExternalLink } from 'lucide-react';
import type { RagSource } from '../types/chat';

interface SourcesModalProps {
  sources: RagSource[];
  onClose: () => void;
}

export function SourcesModal({ sources, onClose }: SourcesModalProps) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[min(80vh,28rem)] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sources-modal-title"
      >
        <div className="flex items-start justify-between gap-3 p-5 border-b border-slate-200 flex-shrink-0">
          <div>
            <h2 id="sources-modal-title" className="text-lg font-semibold text-slate-900">
              Parent documents for this reply
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              Indexed chunks from these markdown files were retrieved and used as context for this
              assistant message.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 transition-colors flex-shrink-0"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <ul className="overflow-y-auto p-5 space-y-2">
          {sources.map((s) => (
            <li key={s.markdown_file_id}>
              <Link
                to={`/documents/${s.markdown_file_id}`}
                onClick={onClose}
                className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-800 transition-colors group"
              >
                <FileText className="w-4 h-4 text-slate-500 flex-shrink-0" />
                <span className="flex-1 text-sm font-medium min-w-0">{s.title}</span>
                <ExternalLink className="w-4 h-4 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
              </Link>
            </li>
          ))}
        </ul>

        <div className="p-4 border-t border-slate-200 flex justify-end flex-shrink-0">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
